import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { payufinSessionStorage } from '@paysense-india-services/payufin-jsutils'

import { ROUTES } from '@/constants/routes'
import { SESSION_STORAGE_KEYS } from '@/constants/sessionStorageKeys'

import { checkSession } from '@/services/authService'

// Normalize path for comparison (pathname may or may not have trailing slash from React Router)
function normalizePath(path) {
  if (!path || path === '/') return '/'
  const p = path.replace(/\/+$/, '') || '/'
  return p === '/' ? '/' : `${p}/`
}

function isLoginRoute(path) {
  const n = normalizePath(path)
  return n === '/' || n === ROUTES.LOGIN || n === ROUTES.OTP
}

function isLogoutRoute(path) {
  return normalizePath(path) === normalizePath(ROUTES.LOGOUT)
}

function shouldSaveRedirectUrl(path) {
  return !isLoginRoute(path) && !isLogoutRoute(path)
}

export function useAuth() {
  const [loading, setLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [user, setUser] = useState(null)

  const navigate = useNavigate()
  const location = useLocation()
  const pathname = location.pathname

  const loginUser = useCallback((muid, userData = null) => {
    setIsAuthenticated(true)
    setUser(userData || { muid })
  }, [])

  const logoutUser = useCallback(async () => {
    setIsAuthenticated(false)
    setUser(null)
    payufinSessionStorage?.setToSession(SESSION_STORAGE_KEYS.AUTH_FROM_URL, null)
    navigate(ROUTES.LOGIN, { replace: true })
  }, [navigate])

  const checkAuthSession = useCallback(async () => {
    try {
      setLoading(true)
      const session = await checkSession()

      if (session?.isActive) {
        setIsAuthenticated(true)
        setUser(session)
      } else {
        setIsAuthenticated(false)
        setUser(null)
      }
    } catch {
      setIsAuthenticated(false)
      setUser(null)
    } finally {
      setLoading(false)
    }
  }, [])

  // Handle redirections
  useEffect(() => {
    if (loading) return

    // to handle logout routes specifically
    if (!isAuthenticated && isLogoutRoute(pathname)) {
      navigate(ROUTES.LOGIN, { replace: true })
      return
    }

    // Redirect unauthenticated users to login (but save their URL)
    if (!isAuthenticated && shouldSaveRedirectUrl(pathname)) {
      payufinSessionStorage?.setToSession(SESSION_STORAGE_KEYS.AUTH_FROM_URL, pathname)
      navigate(ROUTES.LOGIN, { replace: true })
      return
    }

    if (!isAuthenticated && normalizePath(pathname) === '/') {
      navigate(ROUTES.LOGIN, { replace: true })
      return
    }

    // Redirect authenticated users from login pages to their destination
    if (isAuthenticated && isLoginRoute(pathname)) {
      const savedUrl = payufinSessionStorage?.getFromSession(SESSION_STORAGE_KEYS.AUTH_FROM_URL)
      const cleanSavedUrl =
        savedUrl === 'null' || savedUrl === null || savedUrl === undefined ? null : savedUrl
      const redirectTo =
        cleanSavedUrl && shouldSaveRedirectUrl(cleanSavedUrl) ? cleanSavedUrl : ROUTES.DASHBOARD
      payufinSessionStorage?.setToSession(SESSION_STORAGE_KEYS.AUTH_FROM_URL, null)
      navigate(redirectTo, { replace: true })
    }
  }, [isAuthenticated, loading, pathname, navigate])

  useEffect(() => {
    checkAuthSession()
  }, [checkAuthSession])

  return {
    isAuthenticated,
    loading,
    user,
    loginUser,
    logoutUser,
    checkAuthSession
  }
}
